import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/hooks/useApi'

interface TenantSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  tenant: any
  onSuccess: () => void
}

interface TenantSettings {
  disable_sms: boolean
  allow_multi_called_default: boolean
  subdomain: string
  plan: string
  settings: {
    branding?: {
      [key: string]: any
    }
  }
}

export function TenantSettingsModal({ isOpen, onClose, tenant, onSuccess }: TenantSettingsModalProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState('')
  const [syncMessage, setSyncMessage] = useState('')
  const [settings, setSettings] = useState<TenantSettings>({
    disable_sms: false,
    allow_multi_called_default: false,
    subdomain: '',
    plan: 'professional',
    settings: {
      branding: {}
    }
  })

  const tenantId = tenant?.tenant_id || (tenant?.pk ? tenant.pk.split('#')[0].replace('t_', '') : '')
  const isSuperAdmin = user?.groups?.includes('SUPER_ADMIN')

  useEffect(() => {
    if (isOpen && tenantId) {
      fetchSettings()
    }
  }, [isOpen, tenantId])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await apiClient.getTenantSettings(tenantId)
      setSettings({
        disable_sms: response.disable_sms || false,
        allow_multi_called_default: response.allow_multi_called_default || false,
        subdomain: response.subdomain || '',
        plan: response.plan || tenant?.plan || 'professional',
        settings: response.settings || { branding: {} }
      })
    } catch (err) {
      setError('Failed to load tenant settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError('')
      
      await apiClient.updateTenantSettings(tenantId, settings)
      onSuccess()
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to save settings'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const updateSettings = (updates: Partial<TenantSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }))
  }



  const handleSyncCognitoUrls = async () => {
    try {
      setSyncing(true)
      setSyncMessage('')
      setError('')
      
      const response = await fetch(`/api/admin/tenant/${tenantId}/settings/sync-cognito-urls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        setSyncMessage('Cognito URLs synced successfully!')
      } else {
        throw new Error('Failed to sync Cognito URLs')
      }
    } catch (err: any) {
      setError('Failed to sync Cognito URLs: ' + (err.message || 'Unknown error'))
    } finally {
      setSyncing(false)
    }
  }



  if (!tenant) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tenant Settings</DialogTitle>
          <DialogDescription>
            Configure settings for {tenant.display_name || tenantId}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">Loading settings...</div>
        ) : (
          <div className="space-y-6">
            {/* Subscription Plan - Super Admin Only */}
            {isSuperAdmin && (
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Plan</CardTitle>
                  <CardDescription>
                    Manage tenant subscription tier and billing
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="plan">Current Plan</Label>
                    <Select
                      value={settings.plan}
                      onValueChange={(value) => updateSettings({ plan: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Trial (14 days free)</SelectItem>
                        <SelectItem value="professional">Professional ($59/month)</SelectItem>
                        <SelectItem value="premium">Premium ($89/month)</SelectItem>
                        <SelectItem value="enterprise">Enterprise ($149/month)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Change the subscription plan for this tenant. AI features are configured separately.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Branding Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Branding & Access</CardTitle>
                <CardDescription>
                  Configure tenant branding and subdomain access
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subdomain">QueueMeIn Subdomain</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="subdomain"
                      placeholder="clientname"
                      value={settings.subdomain || ''}
                      readOnly
                      disabled
                      className="flex-1 bg-gray-50"
                    />
                    <span className="text-sm text-gray-500">.queuemein.com</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Subdomain is set during tenant creation and cannot be changed.
                  </p>
                  {settings.subdomain && (
                    <p className="text-xs text-blue-600">
                      Admin URL: <strong>{settings.subdomain}.queuemein.com</strong>
                    </p>
                  )}
                </div>
                
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label>Cognito Authentication</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSyncCognitoUrls}
                        disabled={syncing}
                      >
                        {syncing ? 'Syncing...' : 'Sync Cognito URLs'}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Restore all tenant callback URLs after deployment. Use if login stops working.
                    </p>
                    {syncMessage && (
                      <p className="text-xs text-green-600">{syncMessage}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* General Settings */}
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Basic tenant configuration options
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-sms">Disable SMS</Label>
                    <p className="text-sm text-gray-500">
                      Temporarily disable all SMS sending for this tenant
                    </p>
                  </div>
                  <Switch
                    id="disable-sms"
                    checked={settings.disable_sms}
                    onCheckedChange={(checked) => updateSettings({ disable_sms: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="multi-called">Allow Multi-Called Default</Label>
                    <p className="text-sm text-gray-500">
                      Allow multiple customers to be called simultaneously by default
                    </p>
                  </div>
                  <Switch
                    id="multi-called"
                    checked={settings.allow_multi_called_default}
                    onCheckedChange={(checked) => updateSettings({ allow_multi_called_default: checked })}
                  />
                </div>
              </CardContent>
            </Card>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded">
                {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}